/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToggleMenu } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { ServiceOptionsDialog } from './ServiceOptionsDialog';
import { timeSeriesActions, timeSeriesSelectors } from '../../store';
import type { TimeSeriesRootState } from '../../store/types';

interface ServiceOptionsButtonConnectProps {
  panelId: string;
}

export const ServiceOptionsButtonConnect: React.FC<
  ServiceOptionsButtonConnectProps
> = ({ panelId }: ServiceOptionsButtonConnectProps) => {
  const dispatch = useDispatch();

  const services = useSelector((store: TimeSeriesRootState) =>
    timeSeriesSelectors.getServices(store),
  );

  const parametersInUse = useSelector((store: TimeSeriesRootState) =>
    timeSeriesSelectors.getParametersInUse(store, panelId),
  );

  const openNewServicePopup = React.useCallback(() => {
    dispatch(
      timeSeriesActions.setServicePopup({
        panelId,
        isOpen: true,
        variant: 'add',
      }),
    );
  }, [dispatch, panelId]);

  const openServicePopup = React.useCallback(
    (variant: 'show' | 'edit', serviceId: string, url: string) => {
      dispatch(
        timeSeriesActions.setServicePopup({
          panelId,
          isOpen: true,
          variant,
          serviceId,
          url,
        }),
      );
    },
    [dispatch, panelId],
  );

  const removeService = React.useCallback(
    (id: string, message?: string) => {
      dispatch(
        timeSeriesActions.removeService({
          panelId,
          id,
        }),
      );
      if (message) {
        dispatch(
          snackbarActions.openSnackbar({
            type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
            message,
          }),
        );
      }
    },
    [dispatch, panelId],
  );

  return (
    <ToggleMenu menuPosition="bottom">
      <ServiceOptionsDialog
        openNewServicePopup={openNewServicePopup}
        openServicePopup={openServicePopup}
        parametersInUse={parametersInUse}
        removeService={removeService}
        services={services}
      />
    </ToggleMenu>
  );
};
